/**
 * Knowledge Audio Guide — Constants
 *
 * Config-driven values with fallbacks to defaults.
 */

import { getConfig } from '../config/project'

// ── API Endpoints (config-driven with fallbacks) ────────────────

export const ARTRACK_API_BASE = getConfig().api.artrackUrl
export const STORAGE_API_BASE = getConfig().api.storageUrl
export const API_KEY = getConfig().api.apiKey

/** Build audio URL from storage ID */
export function audioUrl(storageId: number): string {
  return `${STORAGE_API_BASE}/storage/media/${storageId}`
}

/** Build knowledge API URL. Without persona/lang the endpoint serves the
 *  legacy DE source doc; with them ArTrack resolves the (persona, lang)
 *  narration cell via Content's resolver incl. fallback cascade. */
export function knowledgeUrl(trackId: number, persona?: string, lang?: string): string {
  const base = `${ARTRACK_API_BASE}/tracks/${trackId}/knowledge`
  const params = new URLSearchParams()
  if (persona) params.set('persona', persona)
  if (lang) params.set('lang', lang)
  const qs = params.toString()
  return qs ? `${base}?${qs}` : base
}

/** Build snap-to-path API URL */
export function snapUrl(trackId: number): string {
  return `${ARTRACK_API_BASE}/snap/${trackId}/snap`
}

// ── Track / Route IDs (config-driven) ───────────────────────────

export const TRACK_ID = getConfig().api.trackId
export const DEFAULT_ROUTE_ID = getConfig().defaultRouteId ?? 56

// ── Zone Detection Defaults ─────────────────────────────────────

export const DEFAULT_APPROACHING_RADIUS = 50
export const DEFAULT_AT_POI_RADIUS = 15
export const DEFAULT_EXIT_RADIUS = 60
export const DEFAULT_COOLDOWN_SECONDS = 60
export const MIN_GPS_ACCURACY = 50

// ── Caching ─────────────────────────────────────────────────────

// Bump the version suffix whenever the knowledge doc's shape or content is
// re-keyed server-side (e.g. ArTrack moved audio cues to new waypoint IDs) —
// old cache keys are then ignored on next load, flushing stale audio lookups.
// v2: 2026-07-01 orphaned-cue re-keying (sights played Siri from a 7-day-stale
// cache that still had the pre-fix audio-less POIs).
export const CACHE_KEY_PREFIX = 'knowledge_track_v2_'
// Short freshness so curator edits propagate on the next visit rather than
// after a week. Combined with the always-on background revalidation below, a
// stale render self-heals within the same session. (was 7 days)
export const CACHE_FRESH_MS = 60 * 60 * 1000

// ── Bounds (config-driven) ──────────────────────────────────────

const config = getConfig()

export const SCHLUCHT_BOUNDS = config.bounds ?? {
  minLat: 46.475, maxLat: 46.500,
  minLng: 14.250, maxLng: 14.295,
}

export function isInSchlucht(lat: number, lng: number): boolean {
  return (
    lat >= SCHLUCHT_BOUNDS.minLat && lat <= SCHLUCHT_BOUNDS.maxLat &&
    lng >= SCHLUCHT_BOUNDS.minLng && lng <= SCHLUCHT_BOUNDS.maxLng
  )
}
